import { NATURAL_CHESTS, SIMPATIA_CHESTS } from "@eldrun/content";
import { Mesh, PerspectiveCamera, PlaneGeometry, Scene, Vector3, type WebGLRenderer } from "three";
import { FrameTimer } from "../engine/frame-loop.ts";
import { FrameStats } from "../engine/frame-stats.ts";
import { StatsHud } from "../engine/stats-hud.ts";
import { PlacementGhost } from "../game/placement.ts";
import { StructureLayer } from "../game/structure-layer.ts";
import { applyAtmosphere, createAtmosphereUniforms, sampleAtmosphere } from "../render/atmosphere.ts";
import { PostFx } from "../render/post/post-fx.ts";
import { countPrograms } from "../render/post/post-model.ts";
import type { ModelLibrary } from "../render/model-library.ts";
import { createFoliageMaterial } from "../render/shaders/foliage.ts";
import { createGhostMaterial } from "../render/shaders/ghost.ts";
import { toonMaterial } from "../render/shaders/toon.ts";

export interface StructureViewer {
  stats(): Record<string, unknown>;
  stop(): void;
}

export type StructureViewerView = "geral" | "estacoes" | "baus" | "base" | "forno" | "fantasma" | "longe";

export interface StructureViewerOptions {
  /**
   * `geral` (tudo), `estacoes`, `baus`, `base` (piso e alicerce), `forno` (a chama na boca), `fantasma` ou
   * `longe` (a base a 140 m, com zoom: as peças da grade no LOD de longe).
   */
  view: StructureViewerView;
  phase: number;
  /** Tampas dos baús abertas (o de madeira e os naturais como para quem os abre; os de Bênção, comprados). */
  open: boolean;
  /** Forno e Panela acesos. */
  lit: boolean;
}

/** Estações na fileira da frente (x em m): as com modelo e o forno e a panela, que ficam procedurais. */
const STATIONS: readonly [string, number][] = [
  ["bancada", -4.6],
  ["bigorna", -2.8],
  ["forno-barro", -1],
  ["panela-barro", 0.8],
  ["bau-madeira", 2.4],
];
/** Baús na fileira do meio: os naturais (ferragens do lugar) e os de Bênção (ferragens da raridade). */
const CHESTS = [...Object.values(NATURAL_CHESTS).map((c) => c.item), ...Object.values(SIMPATIA_CHESTS).map((c) => c.item)];

const CAMERAS: Record<StructureViewerView, { position: readonly [number, number, number]; target: readonly [number, number, number]; fov?: number }> = {
  geral: { position: [1, 4.6, 6.6], target: [1, 0, -3.8] },
  estacoes: { position: [-1.2, 2.1, 4.2], target: [-1.2, 0.5, 0] },
  baus: { position: [0, 1.55, -0.7], target: [0, 0.25, -2.6] },
  base: { position: [-5.2, 4.2, -2.4], target: [0, 0.2, -7.6] },
  forno: { position: [-0.6, 1.25, 1.9], target: [-1, 0.45, 0] },
  fantasma: { position: [5.3, 2.6, 3], target: [5.3, 0.3, -1.4] },
  longe: { position: [-60, 40, 118], target: [0, 0.2, -7.5], fov: 5 },
};

/**
 * Vitrine das estruturas (`?debug=estruturas`, F48, sem servidor): as estações (bancada e bigorna com
 * modelo; forno e panela procedurais, acesos), os baús (o de madeira, os naturais e os de Bênção, com a
 * tampa aberta ou fechada), uma base pequena (alicerces, pisos e as peças que continuam procedurais) e
 * os fantasmas de construção verde e vermelho. `&modelos=0` mostra a reserva procedural.
 */
export function startStructureViewer(renderer: WebGLRenderer, library: ModelLibrary, options: StructureViewerOptions): StructureViewer {
  const scene = new Scene();
  const atmosphere = createAtmosphereUniforms();
  const sample = sampleAtmosphere(options.phase);
  scene.background = sample.horizon;
  const framing = CAMERAS[options.view];
  const camera = new PerspectiveCamera(framing.fov ?? 60, 1, 0.1, 400);
  camera.position.fromArray(framing.position);
  camera.lookAt(new Vector3().fromArray(framing.target));
  const ground = new Mesh(new PlaneGeometry(80, 80), toonMaterial(atmosphere, "#7FA34E", 0));
  ground.rotation.x = -Math.PI / 2;
  scene.add(ground);
  const foliage = createFoliageMaterial(atmosphere, { outline: true });
  const flame = toonMaterial(atmosphere, "#FF8A2A", 1, true);
  const layer = new StructureLayer(scene, foliage, flame, { library });
  const timer = new FrameTimer();
  const hud = new StatsHud(document.body);
  const resize = () => {
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
  };
  window.addEventListener("resize", resize);
  resize();

  // Ids numéricos, como os do servidor (a chama tremula pela fase do id).
  let n = 1;
  const place = (itemId: string, x: number, y: number, z: number, yaw = 0, open = false) => {
    const id = String(n++);
    layer.add(id, { itemId, x, y, z, yaw, open });
    return id;
  };
  for (const [itemId, x] of STATIONS) {
    const id = place(itemId, x, 0, 0);
    if (itemId === "forno-barro" || itemId === "panela-barro") layer.setLit(id, options.lit);
    if (itemId === "bau-madeira") layer.setLocalOpen(id, options.open);
  }
  CHESTS.forEach((itemId, i) => {
    const simpatia = i >= Object.keys(NATURAL_CHESTS).length;
    const id = place(itemId, (i - (CHESTS.length - 1) / 2) * 1.15, 0, -2.6, 0, simpatia && options.open);
    if (!simpatia) layer.setLocalOpen(id, options.open);
  });
  // Base pequena: três alicerces saindo 0,6 m do chão na frente e três pisos rentes atrás, com as peças
  // que continuam procedurais em volta (uma parede de cada tipo e a porta no fundo, a rampa subindo para
  // os alicerces e a estacada do lado).
  const level = 0.6;
  for (let i = 0; i < 3; i++) {
    place("alicerce-pedra", -2 + i * 2, level, -6);
    place("piso-tabuas", -2 + i * 2, 0.05, -8);
  }
  place("parede-tabuas", -2, 0.05, -9);
  place("parede-taipa", 0, 0.05, -9);
  place("parede-porta", 2, 0.05, -9);
  place("rampa-tabuas", 4, 0, -6, Math.PI / 2);
  place("estacada", -3, 0.05, -8, Math.PI / 2);

  // Fantasmas: o baú (com a tampa fechada) e o piso, verde onde pode e vermelho onde não.
  const ghosts = [
    { item: "bau-madeira", x: 3.8, z: -0.2, ok: true },
    { item: "bau-madeira", x: 5, z: -0.2, ok: false },
    { item: "bancada", x: 6.6, z: -0.2, ok: false },
    { item: "piso-tabuas", x: 6.3, z: -2.8, ok: true },
  ].map((g) => {
    const ghost = new PlacementGhost(scene, createGhostMaterial(atmosphere), library);
    ghost.show(g.item, g.x, g.item === "piso-tabuas" ? 0.05 : 0, g.z, 0, g.ok);
    return ghost;
  });

  // F52: a contagem do quadro e o pós-processamento (`&pos=`; padrão, o Médio).
  const counter = new FrameStats(renderer.info);
  const unwatch = counter.watch(scene);
  const post = new PostFx(renderer);
  post.bind(atmosphere);
  let last = performance.now();
  renderer.setAnimationLoop((now: number) => {
    counter.begin();
    const dt = Math.min(0.1, (now - last) / 1000);
    timer.push(now - last);
    last = now;
    const t = now / 1000;
    applyAtmosphere(atmosphere, sample, t);
    layer.update(t, camera.position.x, camera.position.z);
    post.render(scene, camera, dt, counter);
    const f = counter.end();
    const s = layer.stats();
    hud.update(now, () => [
      `Eldrun · estruturas · ${timer.fps.toFixed(0)} fps · draw calls ${f.drawCalls} (+${f.postCalls} do pós)`,
      `modelos ${s.models} · procedurais ${s.procedural} · tampas ${s.open}/${s.lids} abertas · acesas ${layer.lit} · lotes ${s.drawCalls}`,
    ]);
  });

  return {
    stats: () => ({
      fps: timer.fps,
      drawCalls: counter.last.drawCalls,
      frameCalls: counter.last.frameCalls,
      programs: countPrograms(renderer.info.programs).scene,
      post: post.stats(counter.last),
      layer: layer.stats(),
      lit: layer.lit,
      ghosts: ghosts.filter((g) => g.visible).length,
      view: options.view,
    }),
    stop() {
      renderer.setAnimationLoop(null);
      post.dispose();
      unwatch();
      window.removeEventListener("resize", resize);
      for (const g of ghosts) g.dispose();
      layer.dispose();
      hud.remove();
      foliage.dispose();
      flame.dispose();
      ground.geometry.dispose();
      (ground.material as { dispose(): void }).dispose();
    },
  };
}
